import { CarouselButton } from '@carvana/ds-button';
import type { EventTrackingValues } from '@carvana/ds-carousel';
import { PaginationDots } from '@carvana/ds-pagination';
import {
  Children,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import {
  Controls,
  PaginationWrapper,
  Slide,
  Slides,
  Wrapper
} from './PageCountCarousel.styles';
import useCarouselTracking from './useCarouselTracking';
import usePageSnap, { type StoredDimensions } from './usePageSnap';

type OwnProps = {
  ariaLabel: string;
  children: ReactNode;
  testId?: string;
  trackEvent?: (values: EventTrackingValues) => void;
};

export default function PageCountCarousel({
  ariaLabel,
  children,
  testId,
  trackEvent
}: OwnProps) {
  const totalNumOfSlides = Children.count(children);
  const carouselRef = useRef<HTMLElement>(null);
  const slidesRef = useRef<HTMLDivElement>(null);
  const storedDimensionsRef = useRef<StoredDimensions>({
    carouselWidth: 0,
    gapWidth: 0,
    slideWidth: 0,
    totalSlidesWidth: 0
  });
  const trackingRef = useRef<
    Omit<EventTrackingValues, 'direction' | 'interactionType'>
  >({
    reachedEnd: false,
    totalSlides: totalNumOfSlides,
    userInteracted: false,
    visibleSlides: 0
  });
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [controlsWidth, setControlsWidth] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isScrollable, setIsScrollable] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [visibleSlides, setVisibleSlides] = useState(0);

  const { trackDirectionEvent } = useCarouselTracking({
    nextDisabled,
    prevDisabled,
    slidesRef,
    trackEvent,
    trackingRef
  });

  const isGroupedSlideMode = visibleSlides > 1;

  const { endPadding, paginationCurrent, paginationTotal, scrollToPage } =
    usePageSnap({
      carouselWidth,
      currentSlide,
      enabled: isGroupedSlideMode,
      slidesRef,
      storedDimensionsRef,
      totalSlides: totalNumOfSlides,
      visibleSlides
    });

  function scrollNext() {
    if (slidesRef.current) {
      if (isGroupedSlideMode) {
        scrollToPage('next');
      } else {
        const { gapWidth, slideWidth } = storedDimensionsRef.current;
        const scrollDistance =
          (slideWidth + gapWidth) * trackingRef.current.visibleSlides;
        slidesRef.current.scrollTo({
          behavior: 'smooth',
          left: slidesRef.current.scrollLeft + scrollDistance
        });
      }
    }
  }

  function scrollPrev() {
    if (slidesRef.current) {
      if (isGroupedSlideMode) {
        scrollToPage('prev');
      } else {
        const { gapWidth, slideWidth } = storedDimensionsRef.current;
        const scrollDistance =
          (slideWidth + gapWidth) * trackingRef.current.visibleSlides;
        slidesRef.current.scrollTo({
          behavior: 'smooth',
          left: slidesRef.current.scrollLeft - scrollDistance
        });
      }
    }
  }

  function handleNextClick() {
    scrollNext();
    trackDirectionEvent('next', 'button');
  }

  function handlePrevClick() {
    scrollPrev();
    trackDirectionEvent('prev', 'button');
  }

  useLayoutEffect(() => {
    function initializeDimensions() {
      const carouselNode = carouselRef.current;
      const slidesNode = slidesRef.current;
      if (!carouselNode || !slidesNode) return;

      const measuredCarouselWidth = Math.ceil(
        carouselNode.getBoundingClientRect().width
      );
      const gapWidth =
        parseInt(getComputedStyle(slidesNode).columnGap, 10) || 0;
      const slides = slidesNode.querySelectorAll(
        '[aria-roledescription="slide"]'
      );
      const totalSlidesWidth = slidesNode.scrollWidth;
      if (slides.length === 0) return;

      // Prefer the first slide's content width, falling back to the slide wrapper
      const firstSlide = slides[0].firstElementChild ?? slides[0];
      const slideWidth = firstSlide.getBoundingClientRect().width;

      setIsScrollable(totalSlidesWidth > measuredCarouselWidth);
      storedDimensionsRef.current = {
        carouselWidth: measuredCarouselWidth,
        gapWidth,
        slideWidth,
        totalSlidesWidth
      };

      const slideWithGapWidth = slideWidth + gapWidth;
      // Account for the last visible slide not needing a trailing gap
      const newVisibleSlides =
        slideWithGapWidth > 0
          ? Math.floor((measuredCarouselWidth + gapWidth) / slideWithGapWidth)
          : 0;
      trackingRef.current.visibleSlides = newVisibleSlides;
      const pageWidth =
        newVisibleSlides > 0
          ? newVisibleSlides * slideWidth + (newVisibleSlides - 1) * gapWidth
          : 0;
      const visibleWidth = slidesNode.clientWidth;
      const isNextSlidePeeking = visibleWidth > pageWidth + gapWidth;
      setControlsWidth(
        isNextSlidePeeking ? visibleWidth : Math.min(pageWidth, visibleWidth)
      );
      setVisibleSlides(newVisibleSlides);
      setCarouselWidth(measuredCarouselWidth);
    }

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        initializeDimensions();
      }, 150);
    });

    initializeDimensions();
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [totalNumOfSlides]);

  useLayoutEffect(() => {
    if (slidesRef.current && visibleSlides > 0) {
      slidesRef.current.dispatchEvent(new Event('scroll'));
    }
  }, [carouselWidth, visibleSlides]);

  useLayoutEffect(() => {
    function handleScroll() {
      const slidesNode = slidesRef.current;
      if (!slidesNode) return;

      const {
        carouselWidth: storedCarouselWidth,
        gapWidth,
        slideWidth
      } = storedDimensionsRef.current;
      const slideWithGap = slideWidth + gapWidth;
      const scrollLeft = slidesNode.scrollLeft;
      // Subtract endPadding from scrollWidth since padding isn't real content
      const totalSlidesWidth = slidesNode.scrollWidth - endPadding;
      const atEnd = scrollLeft + storedCarouselWidth >= totalSlidesWidth;

      setPrevDisabled(scrollLeft <= 0);
      setNextDisabled(atEnd);

      if (atEnd) {
        trackingRef.current.reachedEnd = true;
        setCurrentSlide(totalNumOfSlides);
      } else {
        const baseSlide = Math.floor(scrollLeft / slideWithGap) + 1;
        const remainingWidth =
          totalSlidesWidth - (scrollLeft + storedCarouselWidth);
        // Is penultimate slide less than 50% in view? Then consider the last slide as current
        setCurrentSlide(
          baseSlide === totalNumOfSlides - 1 &&
            1 - remainingWidth / slideWithGap > 0.5
            ? totalNumOfSlides
            : baseSlide
        );
      }
    }

    let scrollFrameId: number | null = null;

    function throttledHandleScroll() {
      if (scrollFrameId !== null) return;
      scrollFrameId = requestAnimationFrame(() => {
        handleScroll();
        scrollFrameId = null;
      });
    }

    const currentSlidesRef = slidesRef.current;
    if (!currentSlidesRef) return;
    currentSlidesRef.addEventListener('scroll', throttledHandleScroll);

    return () => {
      if (scrollFrameId !== null) cancelAnimationFrame(scrollFrameId);
      currentSlidesRef.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [endPadding, totalNumOfSlides]);

  // With page snapping, derive button states from page position to avoid endPadding timing issues on resize
  const isPrevDisabled = isGroupedSlideMode
    ? paginationCurrent <= 1
    : prevDisabled;
  const isNextDisabled = isGroupedSlideMode
    ? paginationCurrent >= paginationTotal
    : nextDisabled;

  // Intentional deviation: DS shows arrows on isScrollable alone; a single page needs no arrows or dots.
  const showControls = isScrollable && totalNumOfSlides > 1;

  return (
    <Wrapper
      aria-label={ariaLabel}
      aria-roledescription="carousel"
      data-testid={testId}
      ref={carouselRef}
      role="region"
    >
      {/* Arrow clicks report through trackEvent; an analytics id here would double-fire via auto click tracking */}
      {showControls ? (
        <Controls
          style={
            controlsWidth > 0
              ? { alignSelf: 'flex-start', width: controlsWidth }
              : undefined
          }
        >
          <CarouselButton
            aria-label="Previous slides"
            data-analytics-id={null}
            data-testid="carousel-prev"
            direction="left"
            disabled={isPrevDisabled}
            onClick={handlePrevClick}
          />
          <CarouselButton
            aria-label="Next slides"
            data-analytics-id={null}
            data-testid="carousel-next"
            direction="right"
            disabled={isNextDisabled}
            onClick={handleNextClick}
          />
        </Controls>
      ) : null}
      <Slides
        dir="ltr"
        ref={slidesRef}
        style={endPadding ? { paddingRight: endPadding } : undefined}
      >
        {Children.map(children, (child, index) => (
          <Slide
            aria-label={`slide ${index + 1} of ${totalNumOfSlides}`}
            aria-roledescription="slide"
            role="group"
            style={isGroupedSlideMode ? { scrollSnapAlign: 'none' } : undefined}
          >
            {child}
          </Slide>
        ))}
      </Slides>
      {showControls ? (
        <PaginationWrapper>
          <PaginationDots
            currentItem={paginationCurrent}
            testId="page-count-carousel-dots"
            totalItems={paginationTotal}
          />
        </PaginationWrapper>
      ) : null}
    </Wrapper>
  );
}
